/**
 * Authentication service for the CrewAI RAG application
 * Handles user authentication, registration, and token management
 */

import axios from 'axios';

// Create a dedicated axios instance for authentication to avoid circular dependency
// (api.js imports authService, so authService cannot import api.js)
const authApi = axios.create({
  // Use empty baseURL to leverage proxy configuration from package.json
  baseURL: '',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  validateStatus: function (status) {
    return status >= 200 && status < 500; // Don't reject on 4xx/5xx
  },
});

/**
 * Service for handling authentication with the backend API
 */
class AuthService {
  /**
   * Login user and retrieve JWT token
   * @param {string} username - User's username
   * @param {string} password - User's password
   * @returns {Promise<Object>} - Response with access token and token type
   */
  async login(username, password) {
    console.log('=== Starting login attempt ===');
    console.log(`Timestamp: ${new Date().toISOString()}`);
    console.log(`Attempting login for user: ${username}`);
    
    try {
      // Create form data
      const loginData = new URLSearchParams();
      loginData.append('username', username);
      loginData.append('password', password);
      
      console.log('Making axios request to /api/token');
      
      const response = await authApi.post('/api/token', loginData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json'
        },
        withCredentials: true
      });

      console.log('=== Received response ===');
      console.log('Status:', response.status);


      if (response.status >= 400) {
        console.error(`Error response received: ${response.status}`);
        const errorMessage = response.data?.detail || 'Login failed';
        throw new Error(errorMessage);
      }

      // Store token in localStorage
      localStorage.setItem('user', JSON.stringify(response.data));
      return response.data;

    } catch (error) {
      console.error('=== Login Error ===');
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      console.error('Stack trace:', error.stack);
      
      if (error.code === 'ECONNABORTED') {
        throw new Error('Login request timed out after 10 seconds');
      }
      
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Login failed with status:', error.response.status);
        throw new Error(error.response.data?.detail || 'Login failed');
      } else if (error.request) {
        // The request was made but no response was received
        console.error('Login server did not respond');
        throw new Error('No response received from server');
      }
      
      // Something happened in setting up the request that triggered an Error
      throw error;
    }
  }

  /**
   * Register a new user
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} - Response with access token and token type
   */
  async register(userData) {
    try {
      console.log('=== Starting registration attempt ===');
      console.log(`Timestamp: ${new Date().toISOString()}`);
      console.log('Attempting to register user:', userData.username);
      
      // Send registration request through proxy - use /signup to match setupProxy.js configuration
      const response = await fetch('/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      console.log('Registration response status:', response.status);
      console.log('Registration response headers:', response.headers);
      
      // Check content type before parsing as JSON
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        // If not JSON, get as text
        const text = await response.text();
        console.error('Non-JSON response received:', text);
        throw new Error(text || 'Registration failed - invalid response format');
      }
      
      if (!response.ok) {
        console.error('Registration error response:', data);
        throw new Error(data.detail || 'Registration failed');
      }

      console.log('Registration completed');
      
      // Store token in localStorage
      localStorage.setItem('user', JSON.stringify(data));
      
      return data;
    } catch (error) {
      console.error('=== Registration Error ===');
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      console.error('Stack trace:', error.stack);
      throw error;
    }
  }

  /**
   * Step 1 of email-verified registration: submit details, backend emails a
   * 6-digit code and stores a pending registration. No account yet.
   * @param {Object} userData - { username, email, password, company_name }
   * @returns {Promise<Object>} - { message }
   */
  async registerStart(userData) {
    const response = await fetch('/api/register/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.detail || 'Could not start registration');
    }
    return data;
  }

  /**
   * Step 2 of email-verified registration: submit the emailed code. On success
   * the account is created and a token returned — the user is logged straight in.
   * @param {string} email
   * @param {string} code - 6-digit code
   * @returns {Promise<Object>} - { access_token, token_type }
   */
  async registerVerify(email, code) {
    const response = await fetch('/api/register/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.detail || 'Could not verify code');
    }
    // Log the new user straight in.
    localStorage.setItem('user', JSON.stringify(data));
    return data;
  }

  /**
   * Ask the backend to email a fresh verification code for a pending sign-up.
   * @param {string} email
   * @returns {Promise<Object>} - { message }
   */
  async registerResend(email) {
    const response = await fetch('/api/register/resend', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.detail || 'Could not resend code');
    }
    return data;
  }

  /**
   * Logout the current user
   */
  logout() {
    localStorage.removeItem('user');
  }

  /**
   * Get the current user from localStorage
   * @returns {Object|null} - Current user data or null if not logged in
   */
  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    
    try {
      return JSON.parse(userStr);
    } catch (e) {
      return null;
    }
  }

  /**
   * Check if user is logged in
   * @returns {boolean} - True if user is logged in
   */
  isLoggedIn() {
    return !!this.getCurrentUser();
  }

  /**
   * Get auth header with JWT token
   * @returns {Object} - Headers object with Authorization header
   */
  getAuthHeader() {
    const user = this.getCurrentUser();
    if (user && user.access_token) {
      return { 'Authorization': `Bearer ${user.access_token}` };
    }
    return {};
  }

  /**
   * Get the current user's company_id from stored auth data
   * @returns {number|null} - Company ID or null if not available
   */
  getCompanyId() {
    const user = this.getCurrentUser();
    return user?.company_id ?? null;
  }

  /**
   * Get the current user's company_name from stored auth data
   * @returns {string|null} - Company name or null if not available
   */
  getCompanyName() {
    const user = this.getCurrentUser();
    return user?.company_name ?? null;
  }

  /**
   * Fetch current user profile from the backend
   * @returns {Promise<Object>} - User profile data
   */
  async fetchCurrentUserProfile() {
    console.log('Fetching current user profile...');
    try {
      const response = await authApi.get('/api/users/me', {
        headers: this.getAuthHeader()
      });
      console.log('User profile response:', response.status);
      if (response.status === 200) {
        return response.data;
      } else {
        throw new Error(`Failed to fetch user profile: ${response.status}`);
      }
    } catch (error) {
      // SEC-12: do not fabricate a profile — surface the failure so the caller
      // (e.g. Header) can fall back to the JWT payload or force re-login.
      console.error('Could not fetch user profile');
      throw error;
    }
  }

  /**
   * Request password reset for an email address
   * @param {string} email - User's email address
   * @returns {Promise<Object>} - Response from the server
   */
  async requestPasswordReset(email) {
    try {
      const response = await fetch('/api/password-reset-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Password reset request failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Password reset request error:', error);
      throw error;
    }
  }

  /**
   * Reset password with token
   * @param {string} token - Password reset token
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} - Response from the server
   */
  async resetPassword(token, newPassword) {
    try {
      const response = await fetch('/api/password-reset-confirm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, new_password: newPassword }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Password reset failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  }
}

const authService = new AuthService();
export default authService;
